// Industry-standard international phone input: a country dial-code selector +
// a national number field, combined into an E.164 string ("+447911123456").
// Styling matches the festival splash's dark glassy pills; onChange emits the
// E.164 value (or "" when empty). Optional by design, the caller decides if
// it's required.
import { useState } from "react";

type Country = { code: string; flag: string; dial: string; name: string };

// Curated common list (UK default), enough for a UK/EU festival audience
// without shipping a 200-country dataset. Easy to extend.
const COUNTRIES: Country[] = [
  { code: "GB", flag: "🇬🇧", dial: "44", name: "United Kingdom" },
  { code: "IE", flag: "🇮🇪", dial: "353", name: "Ireland" },
  { code: "US", flag: "🇺🇸", dial: "1", name: "United States" },
  { code: "FR", flag: "🇫🇷", dial: "33", name: "France" },
  { code: "DE", flag: "🇩🇪", dial: "49", name: "Germany" },
  { code: "ES", flag: "🇪🇸", dial: "34", name: "Spain" },
  { code: "NL", flag: "🇳🇱", dial: "31", name: "Netherlands" },
  { code: "IT", flag: "🇮🇹", dial: "39", name: "Italy" },
  { code: "PT", flag: "🇵🇹", dial: "351", name: "Portugal" },
  { code: "BE", flag: "🇧🇪", dial: "32", name: "Belgium" },
  { code: "PL", flag: "🇵🇱", dial: "48", name: "Poland" },
  { code: "SE", flag: "🇸🇪", dial: "46", name: "Sweden" },
  { code: "DK", flag: "🇩🇰", dial: "45", name: "Denmark" },
  { code: "AU", flag: "🇦🇺", dial: "61", name: "Australia" },
];

export function PhoneInput({
  onChange,
  height = "3.25rem",
  disabled,
}: {
  onChange: (e164: string) => void;
  height?: string;
  disabled?: boolean;
}) {
  const [dial, setDial] = useState("44");
  const [num, setNum] = useState("");

  function emit(nextDial: string, nextNum: string) {
    // Strip non-digits + a leading national trunk 0 (UK/most EU) → national
    // significant number, then prefix the dial code for E.164.
    const digits = nextNum.replace(/\D/g, "").replace(/^0+/, "");
    onChange(digits ? `+${nextDial}${digits}` : "");
  }

  return (
    <div
      style={{
        display: "flex",
        height,
        minHeight: height,
        boxSizing: "border-box",
        flexShrink: 0,
        borderRadius: "13px",
        border: "1px solid rgba(255,255,255,0.28)",
        background: "rgba(0,0,0,0.4)",
        overflow: "hidden",
      }}
      className="backdrop-blur-md"
    >
      <select
        aria-label="Country dialling code"
        value={dial}
        disabled={disabled}
        onChange={(e) => {
          setDial(e.target.value);
          emit(e.target.value, num);
        }}
        style={{
          border: "none",
          borderRight: "1px solid rgba(255,255,255,0.18)",
          background: "#141417",
          color: "#fff",
          padding: "0 0.6rem",
          fontSize: "0.95rem",
          outline: "none",
          cursor: "pointer",
        }}
      >
        {COUNTRIES.map((c) => (
          <option key={c.code} value={c.dial} style={{ background: "#141417", color: "#fff" }}>
            {c.flag} +{c.dial}
          </option>
        ))}
      </select>
      <input
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        value={num}
        disabled={disabled}
        onChange={(e) => {
          setNum(e.target.value);
          emit(dial, e.target.value);
        }}
        placeholder="7911 123456"
        aria-label="Phone number"
        className="min-w-0 flex-1 placeholder-white/45 outline-none"
        style={{
          border: "none",
          background: "transparent",
          padding: "0 1rem",
          fontSize: "1rem",
          color: "#fff",
        }}
      />
    </div>
  );
}
